import React, { useState, useEffect } from 'react';
import Button from "react-bootstrap/Button";
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';
import TransferService from '../../../../services/TransferService';
import PaymentDirectivesModal from '../PaymentDirectivesModal';

// Fonction de formatage des montants
const formatMoney = (amount, currency) => {
    if (amount === null || amount === undefined || amount === '') return 'N/A';

    const number = typeof amount === 'string'
        ? parseFloat(amount.replace(/\s/g, '').replace(',', '.'))
        : amount;

    if (isNaN(number)) return 'N/A';

    const hasDecimal = number % 1 !== 0;

    const formattedAmount = number.toLocaleString('fr-FR', {
        minimumFractionDigits: hasDecimal ? 2 : 0,
        maximumFractionDigits: hasDecimal ? 2 : 0
    });

    return currency ? `${formattedAmount} ${currency}` : formattedAmount;
};

const Step5Form = ({ formData, recipientInfo, selectedAccount, onBack, onSubmit }) => {
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [hasSubmitted, setHasSubmitted] = useState(false);
    const [showDirectivesModal, setShowDirectivesModal] = useState(false);
    const [submissionTimer, setSubmissionTimer] = useState(null);

    const filterEmptyFields = (data) => {
        return Object.keys(data).reduce((acc, key) => {
            if (data[key] !== null && data[key] !== '' && data[key] !== 0) {
                acc[key] = data[key];
            }
            return acc;
        }, {});
    };

    const filterAndRenameRecipientInfo = (data) => {
        const { recipientAccountInfo, ...rest } = data;
        const renamedInfo = {
            Prénom: rest.firstName,
            Nom: rest.lastName,
            Pays: rest.country,
            Ville: rest.city,
            Email: rest.email,
        };
        return renamedInfo;
    };

    const mapWithdrawalMethod = (method) => {
        switch (method) {
            case 'MOBILE MONEY':
                return 'MTN_MOBILE_MONEY';
            case 'Orange Money':
                return 'ORANGE_MONEY';
            case 'BANK':
                return method;
            default:
                return method;
        }
    };

    const filterAndRenameFormData = (data) => {
        const { recipientId, recipientBankAccountInfo, convertedAmount, localCurrency, ...rest } = data;
        const renamedInfo = {
            'Banque émettrice': rest.issuerBankName,
            Pays: rest.country,
            'Méthode de retrait': rest.withdrawalMethod,
            'Nom de l\'émetteur': rest.issuerName,
            'Email de l\'émetteur': rest.issuerEmail,
            'Montant de la transaction': formatMoney(rest.transactionAmount, rest.currency || 'CAD'),
            'Montant converti': formatMoney(convertedAmount, localCurrency || (rest.country === "Cameroun" ? "XAF" : "XOF")),
        };
        return renamedInfo;
    };

    const translateAccountField = (key) => {
        const translations = {
            accountNumber: 'Numéro de compte',
            accountType: 'Type de compte',
            bankName: 'Nom de la banque',
            branchCode: 'Code agence',
            branchName: 'Nom de l\'agence',
            city: 'Ville',
            country: 'Pays',
            currency: 'Devise',
            recipientName: 'Nom du bénéficiaire',
            accountName: 'Nom du compte',
            accountKey: 'Clé du compte',
            phoneNumber: 'Numéro de téléphone',
            walletType: 'Type de portefeuille'
        };
        return translations[key] || key;
    };

    const prepareTransferRequestDTO = () => {
        return {
            issuerBankName: formData.issuerBankName || null,
            country: formData.country || null,
            withdrawalMethod: mapWithdrawalMethod(formData.withdrawalMethod) || null,
            issuerName: formData.issuerName || null,
            issuerEmail: formData.issuerEmail || null,
            userName: formData.userName || null,
            transactionAmount: formData.transactionAmount
                ? Number(formData.transactionAmount)
                : null,
            transactionReceiverAmount: formData.convertedAmount
                ? Number(formData.convertedAmount)
                : null,
            referenceNumber: formData.referenceNumber || null,
            walletAccountNumber: formData.walletAccountNumber || null,
            bankAccountNumber: formData.bankAccountNumber || null,
            recipientBankAccountInfo: formData.recipientBankAccountInfo
                ? Number(formData.recipientBankAccountInfo)
                : null,
            recipientWalletAccountInfo: formData.recipientWalletAccountInfo
                ? Number(formData.recipientWalletAccountInfo)
                : null,
            recipientId: formData.recipientId
                ? Number(formData.recipientId)
                : null
        };
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (hasSubmitted || isSubmitting) {
            toast.warning('Ce transfert a déjà été initié', {
                position: "top-center",
                autoClose: 3000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
            return;
        }

        try {
            setIsSubmitting(true);

            // Créer une promesse pour représenter le délai minimum de 3 secondes
            const minDelay = new Promise(resolve => {
                const timer = setTimeout(() => {
                    resolve();
                }, 3000);
                setSubmissionTimer(timer);
            });

            // Préparer la requête de transfert
            const transferRequestDTO = prepareTransferRequestDTO();
            ////console.log("Sending transfer request:", transferRequestDTO);

            // Exécuter la requête API et le délai minimum en parallèle
            const [response] = await Promise.all([
                TransferService.initTransfer(transferRequestDTO),
                minDelay
            ]);

            ////console.log("Transfer response:", response);

            // Marquer le transfert comme soumis et afficher la modal des directives
            setHasSubmitted(true);
            setShowDirectivesModal(true);

        } catch (error) {
            console.error("Error submitting transfer:", error);
            setIsSubmitting(false);
            setHasSubmitted(false);

            const errorMessage = error.response?.data?.message || 'Erreur lors de la soumission du transfert. Veuillez réessayer.';
            toast.error(errorMessage, {
                position: "top-center",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
            });
        }
    };

    const handleAcknowledgeDirectives = () => {
        setShowDirectivesModal(false);
        toast.success('Transfert soumis avec succès !', {
            position: "top-center",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true
        });

        // Rediriger l'utilisateur après la fermeture de la modal
        setTimeout(() => {
            navigate('/transfer');
        }, 3000);
    };

    // Nettoyage du timer si le composant est démonté
    useEffect(() => {
        return () => {
            if (submissionTimer) {
                clearTimeout(submissionTimer);
            }
        };
    }, [submissionTimer]);

    const filteredRecipientInfo = recipientInfo ? filterAndRenameRecipientInfo(recipientInfo) : null;
    const filteredFormData = filterAndRenameFormData(formData);

    const filterSelectedAccount = (account) => {
        const { id, recipientAccountInfoId, ...rest } = account;
        return rest;
    };

    return (
        <div className="step5-form">
            <ToastContainer />
            <h2>Récapitulatif des informations</h2>
            <div className="summary-info">
                {Object.entries(filteredFormData).map(([key, value]) => (
                    <p key={key} className="summary-item">
                        <strong>{key}:</strong> {value}
                    </p>
                ))}

                {filteredRecipientInfo && (
                    <>
                        <h3 className="section-title">Informations du destinataire</h3>
                        <div className="recipient-info">
                            {Object.entries(filteredRecipientInfo).map(([key, value]) => (
                                <p key={key} className="summary-item">
                                    <strong>{key}:</strong> {value}
                                </p>
                            ))}
                        </div>
                    </>
                )}

                {selectedAccount && (
                    <>
                        <h3 className="section-title">Informations du compte sélectionné</h3>
                        <div className="account-info">
                            {Object.entries(filterSelectedAccount(selectedAccount)).map(([key, value]) => (
                                <p key={key} className="summary-item">
                                    <strong>{translateAccountField(key)}:</strong> {value}
                                </p>
                            ))}
                        </div>
                    </>
                )}
            </div>

            <div className="form-actions" style={{paddingBottom: '30px'}}>
                {!isSubmitting && !hasSubmitted && (
                    <Button
                        variant="secondary"
                        onClick={onBack}
                        className="back-button"
                    >
                        Retour
                    </Button>
                )}
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isSubmitting || hasSubmitted}
                    className="submit-button"
                >
                    {isSubmitting ? (
                        <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Traitement en cours...
                        </>
                    ) : hasSubmitted ? (
                        'Transfert initié'
                    ) : (
                        'Soumettre'
                    )}
                </Button>
            </div>

            {/* Modal pour les directives de paiement */}
            <PaymentDirectivesModal
                show={showDirectivesModal}
                onAcknowledge={handleAcknowledgeDirectives}
            />

            <style jsx>{`
                .step5-form {
                    padding: 20px;
                    border-radius: 8px;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
                }

                .summary-info {
                    margin: 20px 0;
                }

                .section-title {
                    margin: 20px 0 10px;
                    color: #333;
                    font-size: 1.2em;
                }

                .summary-item {
                    margin: 10px 0;
                    padding: 5px 0;
                    border-bottom: 1px solid #eee;
                    word-wrap: break-word;
                    overflow-wrap: break-word;
                    white-space: pre-wrap;
                }

                .form-actions {
                    display: flex;
                    gap: 15px;
                    justify-content: center;
                    margin-top: 30px;
                }

                .back-button, .submit-button {
                    min-width: 120px;
                    padding: 10px 20px;
                }

                .submit-button:disabled {
                    opacity: 0.7;
                    cursor: not-allowed;
                }
            `}</style>
        </div>
    );
};

export default Step5Form;